import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext';

export default function ConstructionLog() {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedID, setCopiedID] = useState(null);

  const { userInfo } = useAuth();
  const companyID = userInfo?.companyID || 1;

  // ESC tipka čisti unos u tražilici
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (searchTerm) {
          setSearchTerm('');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchTerm]);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');

    fetch(`${API_BASE_URL}/contractorList?companyID=${companyID}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json().catch(() => []))
      .then(data => {
        setContractors(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error("Greška pri dohvaćanju kooperanata:", err);
      })
      .finally(() => setLoading(false));
  }, [companyID]);

  // Ikone struka
  const getIkonaStruke = (specialty = '') => {
    const spec = specialty.toLowerCase();
    if (spec.includes('struja') || spec.includes('elektri')) return 'bolt';
    if (spec.includes('voda') || spec.includes('vodovod') || spec.includes('sanitar')) return 'faucet';
    if (spec.includes('keramik') || spec.includes('pod')) return 'grid_on';
    if (spec.includes('pitur') || spec.includes('boja') || spec.includes('fasad')) return 'format_paint';
    if (spec.includes('stolar') || spec.includes('drvo')) return 'carpenter';
    return 'construction';
  };

  // Kopiranje tajnog linka
  const handleCopy = (e, secret, contractorID) => {
    e.preventDefault();
    e.stopPropagation();
    
    const fullLink = `https://developerc.dbtouch.com/go/${secret}`;
    
    navigator.clipboard.writeText(fullLink)
      .then(() => {
        setCopiedID(contractorID);
        setTimeout(() => setCopiedID(null), 2000);
      })
      .catch(err => console.error("Greška pri kopiranju:", err));
  };

  // Filtriranje kooperanata
  const filteredContractors = contractors.filter(c => {
    const name = (c.name || '').toLowerCase();
    const spec = (c.specialty || '').toLowerCase();
    const contact = (c.contactPerson || '').toLowerCase();
    const term = searchTerm.toLowerCase();

    return name.includes(term) || spec.includes(term) || contact.includes(term);
  });

  return (
    <div className="max-w-6xl space-y-6 pb-12">
      
      {/* 1. ZAGLAVLJE */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Izvođači radova</h1>
          <p className="text-xs text-slate-400 font-medium mt-0.5">Baza kooperanata i pristupni linkovi za terenski portal</p>
        </div>

        <span className="text-xs font-bold bg-white border border-slate-200 px-3 py-1.5 rounded-xl text-slate-600 shadow-2xs">
          Ukupno: <strong className="text-slate-900">{filteredContractors.length}</strong>
        </span>
      </div>

      {/* 2. ČISTA TRAKA ZA PRETRAGU */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs">
        <div className="relative w-full sm:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
            search
          </span>
          <input 
            type="text" 
            placeholder="Pretraži po imenu, struci ili kontaktu..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3.5 py-2 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-slate-400 transition" 
          />
        </div>
      </div>

      {/* 3. MREŽA KARTICA KOOPERANATA */}
      {loading ? (
        <p className="text-slate-400 text-center py-16 text-xs font-semibold">Učitavanje izvođača...</p>
      ) : filteredContractors.length === 0 ? (
        <div className="bg-white p-16 rounded-2xl border border-slate-200 shadow-xs text-center">
          <p className="text-xs text-slate-400 font-medium">Nema pronađenih izvođača radova prema unesenom tekstu.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredContractors.map(izv => (
            <div 
              key={izv.contractorID} 
              className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs hover:border-slate-300 transition flex flex-col justify-between space-y-4"
            >
              <div>
                {/* Zaglavlje kartice */}
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-2xl">
                      {getIkonaStruke(izv.specialty)}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <h3 className="font-bold text-sm text-slate-900 truncate">{izv.name}</h3>
                    <span className="inline-block text-[10px] font-extrabold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md mt-0.5">
                      {izv.specialty || 'Opći radovi'}
                    </span>
                  </div>
                </div>

                {/* Kontakt podaci */}
                <div className="mt-4 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-600">
                  {izv.contactPerson && (
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-sm text-slate-400 shrink-0">person</span>
                      <span className="font-medium">{izv.contactPerson}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-slate-400 shrink-0">call</span>
                    {izv.phoneNumber ? (
                      <a href={`tel:${izv.phoneNumber}`} className="hover:text-indigo-600 font-medium truncate">
                        {izv.phoneNumber}
                      </a>
                    ) : (
                      <span className="text-slate-400 italic">Bez telefona</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-slate-400 shrink-0">mail</span>
                    {izv.email ? (
                      <a href={`mailto:${izv.email}`} className="hover:text-indigo-600 font-medium truncate">
                        {izv.email}
                      </a>
                    ) : (
                      <span className="text-slate-400 italic">Bez e-maila</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Portal za kooperanta */}
              <div className="pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between gap-2 bg-slate-50 p-2 rounded-xl border border-slate-200/80">
                  {izv.secret ? (
                    <>
                      <a 
                        href={`https://developerc.dbtouch.com/go/${izv.secret}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-bold truncate flex items-center gap-1.5 min-w-0"
                        title="Otvori terenski portal"
                      >
                        <span className="material-symbols-outlined text-sm text-indigo-400 shrink-0">open_in_new</span>
                        <span className="truncate">go/{izv.secret}</span>
                      </a>

                      <button
                        type="button"
                        onClick={(e) => handleCopy(e, izv.secret, izv.contractorID)}
                        className={`text-[11px] font-bold px-2.5 py-1 rounded-lg transition shrink-0 cursor-pointer ${
                          copiedID === izv.contractorID 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-white hover:bg-slate-200 text-slate-700 border border-slate-200'
                        }`}
                      >
                        {copiedID === izv.contractorID ? 'Kopirano ✓' : 'Kopiraj link'}
                      </button>
                    </>
                  ) : (
                    <span className="text-[11px] text-rose-500 italic font-medium px-1">
                      Nema tajnog ključa
                    </span>
                  )}
                </div>
              </div>

            </div>
          ))}
        </div>
      )}

    </div>
  );
}